import type {SafeHTMLString} from '@github-ui/safe-html'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useClientValue} from '@github-ui/use-client-value'
import {AlertIcon} from '@primer/octicons-react'
import {Button, Flash, Link} from '@primer/react'
import {Octicon, Tooltip} from '@primer/react/deprecated'
import type React from 'react'

import styles from './HiddenUnicode.module.css'

// TODO: there is a new version of this in @github-ui/hidden-unicode-banner, that implementation changes things to use
// state instead of the URL to determine if hidden characters are shown. We should switch the code view to use that

// There are 9 unicode codepoints in two contiguous blocks that act as
// bidi (bidirectional) control characters and 2 other codepoints that
// produce unrenderable text which can obscure malicious code:
// +-----------+----------------------------------+
// | codepoint | Control character name           |
// +-----------+----------------------------------+
// |  \u202A   | LEFT-TO-RIGHT EMBEDDING (LRE)    |
// |  \u202B   | RIGHT-TO-LEFT EMBEDDING (RLE)    |
// |  \u202C   | POP DIRECTIONAL FORMATTING (PDF) |
// |  \u202D   | LEFT-TO-RIGHT OVERRIDE (LRO)     |
// |  \u202E   | RIGHT-TO-LEFT OVERRIDE (RLO)     |
// |        [ ... ].                              |
// |  \u2066   | LEFT-TO-RIGHT ISOLATE (LRI)      |
// |  \u2067   | RIGHT-TO-LEFT ISOLATE (RLI)      |
// |  \u2068   | FIRST STRONG ISOLATE (FSI)       |
// |  \u2069   | POP DIRECTIONAL ISOLATE (PDI)    |
// |  \uE0001  | LANGUAGE TAG (TAG)               |
// |  \uE007F  | CANCEL TAG (TAG)                 |
// +-----------+----------------------------------+
const hiddenUnicodeRegex = /[\u202A-\u202E]|[\u2066-\u2069]|\u{E0001}|\u{E007F}/u
const hiddenUnicodeRegexG = /[\u202A-\u202E]|[\u2066-\u2069]|\u{E0001}|\u{E007F}/gu
const hiddenUnicodeBoundaryRegex = /([\u202A-\u202E]|[\u2066-\u2069]|\u{E0001}|\u{E007F})/gu
const hiddenUnicodeReplacements = {
  '\u202A': 'U+202A', // LEFT-TO-RIGHT EMBEDDING
  '\u202B': 'U+202B', // RIGHT-TO-LEFT EMBEDDING
  '\u202C': 'U+202C', // POP DIRECTIONAL FORMATTING
  '\u202D': 'U+202D', // LEFT-TO-RIGHT OVERRIDE
  '\u202E': 'U+202E', // RIGHT-TO-LEFT OVERRIDE
  '\u2066': 'U+2066', // LEFT-TO-RIGHT ISOLATE
  '\u2067': 'U+2067', // RIGHT-TO-LEFT ISOLATE
  '\u2068': 'U+2068', // FIRST STRONG ISOLATE
  '\u2069': 'U+2069', // POP DIRECTIONAL ISOLATE
  '\u{E0001}': 'U+E0001', // LANGUAGE TAG
  '\u{E007F}': 'U+E007F', // CANCEL TAG
} as const
type HiddenUnicodeChar = keyof typeof hiddenUnicodeReplacements
type HiddenUnicodeReplacement = (typeof hiddenUnicodeReplacements)[HiddenUnicodeChar]
const hiddenUnicodeReplacementMap = new Map<string, HiddenUnicodeReplacement>(Object.entries(hiddenUnicodeReplacements))

export function HiddenUnicodeAlert() {
  //doing it this way so that there are not hydration errors
  const [usableWindow] = useClientValue(() => ssrSafeWindow, ssrSafeWindow, [])

  if (!usableWindow) return null
  const revealButtonUrl = new URL(usableWindow.location.href, usableWindow.location.origin)
  const shown = revealButtonUrl.searchParams.get('h') === '1'

  if (shown) {
    revealButtonUrl.searchParams.delete('h')
  } else {
    revealButtonUrl.searchParams.set('h', '1')
  }

  return (
    <Flash full variant="warning" className={styles.Flash}>
      <Octicon icon={AlertIcon} />
      <span>
        This file contains bidirectional or hidden Unicode text that may be interpreted or compiled differently than
        what appears below. To review, open the file in an editor that reveals hidden Unicode characters.{' '}
        <Link inline href="https://github.co/hiddenchars" target="_blank" rel="noreferrer">
          Learn more about bidirectional Unicode characters
        </Link>
      </span>
      <Button
        as="a"
        onClick={() => {
          window.location.href = revealButtonUrl.href
        }}
        size="small"
        className={styles.Button}
      >
        {shown ? 'Hide revealed characters' : 'Show hidden characters'}
      </Button>
    </Flash>
  )
}

export function HiddenUnicodeTooltip() {
  return (
    <Tooltip direction="e" text="This line has hidden Unicode characters">
      <Octicon icon={AlertIcon} className={styles.Octicon} />
    </Tooltip>
  )
}

function HiddenUnicodeCharacter({char}: {char: HiddenUnicodeReplacement}) {
  return <span className="hidden-unicode-replacement padded">{char}</span>
}

function hiddenUnicodeCharacterHtmlString(hiddenUnicodeReplacementString: HiddenUnicodeReplacement): SafeHTMLString {
  // We purposely don't want to escape the HTML because we know that the string will be from the replacementMap
  // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string
  return `<span class="hidden-unicode-replacement">${hiddenUnicodeReplacementString}</span>` as SafeHTMLString
  // Casting to SafeHTMLString is safe because we know this exact html,
  // and we know that `hiddenUnicodeReplacementString` can only have a few specific values
}

export function hiddenUnicodeCharacterHTMLString(
  hiddenUnicodeReplacementString: HiddenUnicodeReplacement,
): SafeHTMLString {
  // We purposely don't want to escape the HTML because we know that the string will be from the replacementMap
  // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string
  return `<span class="hidden-unicode-replacement" data-code-text="${hiddenUnicodeReplacementString}"></span>` as SafeHTMLString
  // Casting to SafeHTMLString is safe because we know this exact html,
  // and we know that `hiddenUnicodeReplacementString` can only have a few specific values
}

/**
 * Split the given text into an array of strings and hidden Unicode control characters.
 */
export function splitAroundHiddenUnicodeCharacters(text: SafeHTMLString): SafeHTMLString[] {
  return text.split(hiddenUnicodeBoundaryRegex) as SafeHTMLString[]
}

export function showHiddenUnicodeCharactersHTML(text: SafeHTMLString): SafeHTMLString | null {
  if (!hasHiddenUnicodeCharacters(text)) {
    return null
  }

  // Split the text into an array of strings and hidden Unicode control characters.
  const splitText = splitAroundHiddenUnicodeCharacters(text)

  const replacedSegments = splitText.map(segment => {
    const replacement: HiddenUnicodeReplacement | undefined = hiddenUnicodeReplacementMap.get(segment)
    if (!replacement) return segment
    return hiddenUnicodeCharacterHtmlString(replacement)
  })

  // Casting to SafeHTMLString is safe because we all we have done is take
  // verified html and replaced dangerous unicode characters with safe strings.
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string */
  return replacedSegments.join('') as SafeHTMLString
}

export function showHiddenUnicodeCharactersRaw(text: string): string {
  if (!hasHiddenUnicodeCharacters(text)) return text

  return text.replaceAll(hiddenUnicodeRegexG, (char: string) => hiddenUnicodeReplacementMap.get(char) ?? '')
}

export function showHiddenUnicodeCharacters(text: SafeHTMLString): React.ReactNode[] | null {
  if (!hasHiddenUnicodeCharacters(text)) {
    return null
  }

  // Split the text into an array of strings and hidden Unicode control characters.
  const splitText = splitAroundHiddenUnicodeCharacters(text)

  return splitText.map((segment, index) => {
    const replacement: HiddenUnicodeReplacement | undefined = hiddenUnicodeReplacementMap.get(segment)
    // eslint-disable-next-line @eslint-react/no-array-index-key
    return replacement ? <HiddenUnicodeCharacter key={index} char={replacement} /> : segment
  })
}

export function hasHiddenUnicodeCharacters(text: string): boolean {
  return hiddenUnicodeRegex.test(text)
}

export function isHiddenUnicodeShown(): boolean {
  if (!ssrSafeWindow) return false
  const url = new URL(ssrSafeWindow.location.href, ssrSafeWindow.location.origin)
  return url.searchParams.get('h') === '1'
}

export function getHiddenUnicodeReplacement(char: string): HiddenUnicodeReplacement | undefined {
  return hiddenUnicodeReplacementMap.get(char)
}

try{ HiddenUnicodeAlert.displayName ||= 'HiddenUnicodeAlert' } catch {}
try{ HiddenUnicodeTooltip.displayName ||= 'HiddenUnicodeTooltip' } catch {}
try{ HiddenUnicodeCharacter.displayName ||= 'HiddenUnicodeCharacter' } catch {}